import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { FaApple, FaGooglePlay, FaBars, FaTimes } from 'react-icons/fa';
import Logo from './Logo';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'How it Works', href: '#how-it-works' },
    { name: 'App Preview', href: '#app-preview' },
    { name: 'Venues', href: '#venues' },
    { name: 'Why datenite', href: '#why-datenite' },
  ];

  const handleNavClick = (href) => {
    if (location.pathname === '/') {
      // Already on home page, just scroll to section
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to home page first, then scroll to section
      window.location.href = `/${href}`;
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <motion.nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-black bg-opacity-80 backdrop-blur-lg border-b border-white border-opacity-10' 
          : 'bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="flex items-center justify-between py-4">
          {/* Logo */}
          <div>
            {/* <Logo size="medium" /> */}
            <img src="/assets/png-logo.png" alt="Logo" className="w-32" />
           
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <motion.button
                key={item.name}
                onClick={() => handleNavClick(item.href)}
                className="text-white hover:text-romantic-pink transition-colors duration-300 font-medium"
                whileHover={{ y: -2 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                {item.name}
              </motion.button>
            ))}
          </div>

          {/* Desktop CTA Buttons */}
          <div className="hidden md:flex items-center space-x-4">
          <a
              href="https://apps.apple.com/gb/app/datenite-app/id1592597688"
              target="_blank"
              rel="noopener noreferrer"
            >
            <motion.button
              className="bg-gradient-to-r from-romantic-purple to-romantic-pink text-white px-4 py-2 rounded-full font-semibold text-sm flex items-center gap-2"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaApple />
              App Store
            </motion.button>
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=uk.co.datenite.app"
              target="_blank"
              rel="noopener noreferrer"
            >
            <motion.button
              className="bg-gradient-to-r from-romantic-blue to-romantic-purple text-white px-4 py-2 rounded-full font-semibold text-sm flex items-center gap-2"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaGooglePlay />
              Google Play
            </motion.button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            className="md:hidden text-white text-2xl"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            whileTap={{ scale: 0.9 }}
          >
            {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
          </motion.button>
        </div>

        {/* Mobile Menu */}
        <motion.div
          className={`md:hidden overflow-hidden ${isMobileMenuOpen ? 'max-h-96' : 'max-h-0'}`}
          initial={false}
          animate={{ 
            maxHeight: isMobileMenuOpen ? 400 : 0,
            opacity: isMobileMenuOpen ? 1 : 0
          }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          <div className="py-4 space-y-4 border-t border-white border-opacity-10">
            {navItems.map((item) => (
              <motion.button
                key={item.name}
                onClick={() => handleNavClick(item.href)}
                className="block text-white hover:text-romantic-pink transition-colors duration-300 font-medium py-2 text-left w-full"
                whileHover={{ x: 10 }}
              >
                {item.name}
              </motion.button>
            ))}
            
            {/* Mobile CTA Buttons */}
            <div className="flex flex-col space-y-3 pt-4">
              <motion.button
                className="bg-gradient-to-r from-romantic-purple to-romantic-pink text-white px-6 py-3 rounded-full font-semibold flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FaApple />
                Download on App Store
              </motion.button>
              
              <motion.button
                className="bg-gradient-to-r from-romantic-blue to-romantic-purple text-white px-6 py-3 rounded-full font-semibold flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FaGooglePlay />
                Get it on Google Play
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.nav>
  );
};

export default Navigation;
